import React, { useState, useEffect } from "react";
import { MoreHorizontal } from "lucide-react";
import ActionMenu, { ActionMenuItem } from "../ui/ActionMenu";
import apiClient from "../../utils/apiClient";
import DataTable from "../Common/DataTable";
import "./QuestionList.scss";

const QuestionList = ({
  filters,
  refreshKey,
  onEdit,
  onDelete,
  mode = "institute",
  selectedIds = [],
  onSelectionChange,
}) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Institute users can't delete library questions; only own-institute rows
  // are selectable for bulk delete. In library mode (tenantadmin) all rows
  // are deletable.
  const isDeletable = (q) => mode === "library" || !!q.instituteId;
  const deletableIds = questions.filter(isDeletable).map((q) => q._id);
  const handleSelectionChange = (ids) => {
    if (typeof onSelectionChange !== "function") return;
    // Keep only IDs that are actually deletable (skip library rows for institute users).
    const allowed = new Set(deletableIds);
    onSelectionChange(ids.filter((id) => allowed.has(id)));
  };

  // Fetch paginated questions with filters
  useEffect(() => {
    const fetchQuestions = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get("/questions/list", {
          params: {
            ...filters,
            page,
            limit: pageSize,
          },
        });
        setQuestions(data.questions);
        setTotalRecords(data.total || 0);
      } catch {
        setQuestions([]);
        setTotalRecords(0);
      }
      setLoading(false);
    };
    fetchQuestions();
  }, [filters, page, pageSize, refreshKey]);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const getDifficultyClass = (difficulty) => {
    if (difficulty === "Hard") return "cqb-difficulty--hard";
    if (difficulty === "Medium") return "cqb-difficulty--medium";
    return "cqb-difficulty--easy";
  };

  const columns = [
    {
      key: "index",
      label: "#",
      width: "50px",
      render: (_, index) => (page - 1) * pageSize + index + 1
    },
    {
      key: "title",
      label: "Title",
      width: "35%",
      render: (q) => (
        <div className="fw-medium" title={q.title}>
          {q.title?.length > 80 ? q.title.substring(0, 80) + "..." : q.title}
        </div>
      )
    },
    {
      key: "categories",
      label: "Categories",
      width: "15%",
      render: (q) => (
        <div className="d-flex flex-wrap gap-1">
          {q.categories?.map((c) => (
            <span className="cqb-tag cqb-tag--category" key={typeof c === 'object' ? c.name : c}>{typeof c === 'object' ? c.name : c}</span>
          ))}
          {(!q.categories || q.categories.length === 0) && "-"}
        </div>
      )
    },
    {
      key: "labels",
      label: "Labels",
      width: "15%",
      render: (q) => (
        <div className="d-flex flex-wrap gap-1">
          {q.labels?.map((l) => (
            <span className="cqb-tag cqb-tag--label" key={l}>{l}</span>
          ))}
          {(!q.labels || q.labels.length === 0) && "-"}
        </div>
      )
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (q) => (
        <span className={`cqb-difficulty ${getDifficultyClass(q.difficulty)}`}>
          {q.difficulty || "-"}
        </span>
      )
    },
    ...(mode === "library"
      ? [{
          key: "isPublic",
          label: "Visibility",
          width: "10%",
          render: (q) => (
            <span className={`cqb-tag ${q.isPublic ? "cqb-tag--public" : "cqb-tag--draft"}`}>
              {q.isPublic ? "Public" : "Draft"}
            </span>
          )
        }]
      : [{
          key: "source",
          label: "Source",
          width: "10%",
          render: (q) => (
            !q.instituteId
              ? <span className="cqb-tag cqb-tag--public">Library</span>
              : <span className="cqb-tag cqb-tag--label">My Institute</span>
          )
        }]),
    {
      key: "actions",
      label: "",
      width: "50px",
      render: (q) => {
        // Institute users see library questions read-only — hide edit/delete.
        if (mode !== "library" && !q.instituteId) {
          return <span className="text-muted small">—</span>;
        }
        return (
          <ActionMenu trigger={<MoreHorizontal size={16} />}>
            <ActionMenuItem onClick={() => onEdit(q)}>Edit</ActionMenuItem>
            <ActionMenuItem danger onClick={() => onDelete(q._id)}>Delete</ActionMenuItem>
          </ActionMenu>
        );
      }
    }
  ];

  const selectable = typeof onSelectionChange === "function";

  return (
    <DataTable
      data={questions}
      columns={columns}
      loading={loading}
      hoverable={true}
      selectable={selectable}
      selectedRows={selectedIds}
      onSelectionChange={handleSelectionChange}
      pagination={{
        page,
        pageSize,
        total: totalRecords,
        onPageChange: setPage
      }}
      emptyMessage="No questions found"
    />
  );
};

export default QuestionList;
